import React, { useCallback, useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Ban, RotateCcw, Trash2, Pencil, Clock, Search, X } from "lucide-react";
import { CaseEditModal } from "./ReopenedCasesPanel";

const RISK_COLORS = {
  emergencial: "bg-red-100 text-red-800 border-red-200",
  alta: "bg-orange-100 text-orange-800 border-orange-200",
  moderada: "bg-amber-100 text-amber-800 border-amber-200",
  programado: "bg-slate-100 text-slate-700 border-slate-200",
};

const relTime = (iso) => {
  if (!iso) return "—";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return `${Math.max(1, Math.floor(diff / 60000))} min atrás`;
    if (h < 48) return `${h}h atrás`;
    return `${Math.floor(h / 24)}d atrás`;
  } catch { return "—"; }
};

const CancelledCasesPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  // Debounce da busca — 350ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = debouncedQ ? { q: debouncedQ } : {};
      const r = await api.get("/admin/cases/cancelled", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar casos cancelados");
    } finally { setLoading(false); }
  }, [debouncedQ]);

  useEffect(() => { load(); }, [load]);

  const restoreCase = async (c) => {
    if (!window.confirm(
      `Restaurar caso ${c.protocol} — ${c.patient_name}?\n\n` +
      "O caso volta ao status anterior ao cancelamento.",
    )) return;
    try {
      const r = await api.post(`/admin/cases/${c.id}/restore`);
      toast.success(`Caso ${c.protocol} restaurado → ${r.data?.restored_to}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao restaurar caso");
    }
  };

  const deleteCase = async (c) => {
    if (!window.confirm(
      `EXCLUIR DEFINITIVAMENTE ${c.protocol} — ${c.patient_name}?\n\n` +
      "Essa ação apaga o registro do banco. Use \"Restaurar\" se quiser recuperar.",
    )) return;
    try {
      await api.delete(`/admin/cases/${c.id}`);
      toast.success(`Caso ${c.protocol} excluído.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir caso");
    }
  };

  if (!data && loading) return <div className="p-8 text-slate-500 text-sm">Carregando casos cancelados…</div>;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="cancelled-cases-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-slate-600" />
            <h2 className="font-display font-bold text-xl text-[#0B2B62]">Casos cancelados</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Auditoria de casos que foram cancelados pelo admin. Você pode revisar o motivo,
            editar dados ou restaurar o caso a qualquer momento.
          </p>
        </div>
        <button onClick={load} data-testid="cancelled-refresh"
          className="text-xs px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 inline-flex items-center gap-1.5">
          <RotateCcw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 border border-slate-200 bg-white rounded-lg">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total cancelados</div>
          <div className="font-display font-bold text-2xl mt-1 text-[#0B2B62]">{data.total}</div>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por protocolo ou nome do paciente…"
          data-testid="cancelled-search"
          className="w-full pl-9 pr-9 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2B62] focus:border-transparent"
        />
        {q && (
          <button onClick={() => setQ("")} data-testid="cancelled-search-clear"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X className="w-4 h-4" />
          </button>
        )}
        {debouncedQ && (
          <div className="mt-1 text-xs text-slate-500">
            {data.total} resultado(s) para "{debouncedQ}"
          </div>
        )}
      </div>

      {data.total === 0 ? (
        <div className="p-8 border border-dashed border-emerald-300 rounded-xl bg-emerald-50 text-emerald-800 text-sm text-center" data-testid="cancelled-empty">
          {debouncedQ
            ? `Nenhum caso cancelado corresponde a "${debouncedQ}".`
            : "Nenhum caso cancelado no momento — tudo em andamento."}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <div className="col-span-3">Caso / Paciente</div>
            <div className="col-span-2">Risco</div>
            <div className="col-span-3">Motivo</div>
            <div className="col-span-2">Cancelado</div>
            <div className="col-span-2 text-right">Ações</div>
          </div>
          {data.cases.map((c) => (
            <div key={c.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-100 items-center text-sm" data-testid={`cancelled-row-${c.protocol}`}>
              <div className="col-span-3">
                <div className="font-semibold text-[#0B2B62]">{c.protocol}</div>
                <div className="text-slate-700 truncate">{c.patient_name}</div>
                <div className="text-[11px] text-slate-500">{c.city}{c.state ? ` / ${c.state}` : ""}</div>
              </div>
              <div className="col-span-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-widest font-semibold ${RISK_COLORS[c.risk_level] || RISK_COLORS.programado}`}>
                  {c.risk_level || "—"}
                </span>
              </div>
              <div className="col-span-3 text-slate-700 text-xs">
                <div className="italic truncate" title={c.cancel_reason || "—"}>{c.cancel_reason || "—"}</div>
                {c.cancelled_by && (
                  <div className="text-[11px] text-slate-500 mt-0.5">por {c.cancelled_by}</div>
                )}
              </div>
              <div className="col-span-2 text-slate-600 text-xs">
                <Clock className="w-3.5 h-3.5 inline-block mr-1 text-amber-600" />
                {relTime(c.cancelled_at)}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1.5 flex-wrap">
                <button onClick={() => setEditingCase(c)}
                  data-testid={`cancelled-edit-${c.protocol}`}
                  title="Editar dados do caso"
                  className="text-xs px-2 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 inline-flex items-center gap-1 text-slate-700">
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </button>
                <button onClick={() => restoreCase(c)}
                  data-testid={`cancelled-restore-${c.protocol}`}
                  title="Restaurar o caso"
                  className="text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold inline-flex items-center gap-1">
                  <RotateCcw className="w-3.5 h-3.5" /> Restaurar
                </button>
                <button onClick={() => deleteCase(c)}
                  data-testid={`cancelled-delete-${c.protocol}`}
                  title="Excluir definitivamente"
                  className="text-xs px-2 py-1.5 border border-red-200 rounded-md hover:bg-red-50 inline-flex items-center gap-1 text-red-600">
                  <Trash2 className="w-3.5 h-3.5" /> Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CaseEditModal
        open={!!editingCase}
        caseObj={editingCase}
        onClose={() => setEditingCase(null)}
        onSaved={load}
      />
    </div>
  );
};

export default CancelledCasesPanel;
